import { useCallback, useEffect, useRef, useState } from 'react';

import { AuthRedirectError } from '@/api/useAuthedClient';

type UseGraphQLMutationOptions<TInput, TData, TContext> = {
  mutationFn: (input: TInput, signal: AbortSignal) => Promise<TData>;
  onSuccess?: (data: TData, input: TInput, ctx: TContext) => void;
  onError?: (err: Error, input: TInput, ctx: TContext) => void;
} & (undefined extends TContext
  ? { onMutate?: (input: TInput) => TContext }
  : { onMutate: (input: TInput) => TContext });

interface UseGraphQLMutationReturn<TInput, TData> {
  mutate: (input: TInput) => Promise<TData | null>;
  isSubmitting: boolean;
  error: Error | null;
}

export function useGraphQLMutation<TInput, TData, TContext = undefined>(
  options: UseGraphQLMutationOptions<TInput, TData, TContext>,
): UseGraphQLMutationReturn<TInput, TData> {
  const { mutationFn, onMutate, onSuccess, onError } = options;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutationFnRef = useRef(mutationFn);
  mutationFnRef.current = mutationFn;
  const onMutateRef = useRef(onMutate);
  onMutateRef.current = onMutate;
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => abortControllerRef.current?.abort();
  }, []);

  const mutate = useCallback(async (input: TInput): Promise<TData | null> => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    // Conditional options type guarantees onMutate is provided whenever
    // TContext is non-undefined, so the cast is sound.
    const ctx = (onMutateRef.current?.(input) ?? undefined) as TContext;
    setIsSubmitting(true);
    setError(null);

    let data: TData;
    let isAuthRedirect = false;
    try {
      data = await mutationFnRef.current(input, controller.signal);
    } catch (err) {
      // Auth0 redirect is in flight — leave the hook in its submitting state
      // and skip onError so consumers don't flash failure UI before the
      // browser navigates to Universal Login.
      if (err instanceof AuthRedirectError) {
        isAuthRedirect = true;
        return null;
      }
      if (err instanceof Error && err.name === 'AbortError') return null;
      if (controller.signal.aborted) return null;
      const coerced = err instanceof Error ? err : new Error('Unknown error');
      setError(coerced);
      onErrorRef.current?.(coerced, input, ctx);
      return null;
    } finally {
      if (!controller.signal.aborted && !isAuthRedirect) {
        setIsSubmitting(false);
      }
    }
    if (controller.signal.aborted) return null;
    try {
      onSuccessRef.current?.(data, input, ctx);
    } catch {
      // onSuccess threw — don't surface as mutation failure or trigger onError.
    }
    return data;
  }, []);

  return { mutate, isSubmitting, error };
}
